import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SpeedCompetitionData, Competitor, QualSlot, FinalsMatch, RunResult,
  getDefaultData, generateQualSchedule,
  computeGenderedRankings, computeQualRankings, autoPopulateBracket,
  formatTime, parseTimeInput, generateId, clearTimes,
} from '../utils/speedCompetition';
import { loadSpeedData, saveSpeedData } from '../firebase/speedService';
import { useCompetition, AdminAccessGate } from '../context/CompetitionContext';

// ─── TimeInput component ─────────────────────────────────────────────────────

interface TimeInputProps {
  result: RunResult;
  onChange: (r: RunResult) => void;
  tabIndex?: number;
}
const TimeInput: React.FC<TimeInputProps> = ({ result, onChange, tabIndex }) => {
  const toDisplay = (r: RunResult): string => {
    if (r.na)  return 'NA';
    if (r.dnf) return 'DNF';
    if (r.time === null) return '';
    if (r.time < 60) return r.time.toFixed(2);
    const mins = Math.floor(r.time / 60);
    const secs = r.time - mins * 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs.toFixed(2)}`;
  };
  const [value, setValue] = useState(() => toDisplay(result));
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep local state in sync when external result changes (e.g. reset)
  useEffect(() => {
    setValue(toDisplay(result));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.time, result.dnf, result.na]);

  const commit = () => onChange(parseTimeInput(value));

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      tabIndex={tabIndex}
      onChange={e => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { commit(); inputRef.current?.blur(); } }}
      placeholder="9.45 / 1:23.45"
      className="w-20 px-2 py-1 border border-gray-300 rounded text-center font-mono text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
    />
  );
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function getName(id: string | null, competitors: Competitor[]): string {
  if (!id) return '—';
  return competitors.find(c => c.id === id)?.name ?? id;
}

// ─── SpeedAdminPage ───────────────────────────────────────────────────────────

type Tab = 'competitors' | 'qualifications' | 'finals';

const SpeedAdminInner: React.FC = () => {
  const { competitionId, metadata } = useCompetition();
  const [data, setData] = useState<SpeedCompetitionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('competitors');
  const [newName, setNewName] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load from Firestore on mount
  useEffect(() => {
    loadSpeedData(competitionId)
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(String(err)); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  // Update local state only — no Firestore write
  const persist = useCallback((d: SpeedCompetitionData) => {
    setData(d);
    setDirty(true);
    setSaved(false);
  }, []);

  // Explicit save to Firestore (triggered by Save button)
  const saveNow = useCallback(() => {
    if (!data) return;
    setSaving(true);
    saveSpeedData(competitionId, data)
      .then(() => { setSaved(true); setDirty(false); setTimeout(() => setSaved(false), 3000); })
      .catch(err => setError(String(err)))
      .finally(() => setSaving(false));
  }, [competitionId, data]);

  // ── Competitor management ────────────────────────────────────────────────

  const addCompetitor = () => {
    if (!data) return;
    const name = newName.trim();
    if (!name) return;
    const updated: SpeedCompetitionData = { ...data, competitors: [...data.competitors, { id: generateId(), name, gender: 'male' as const }] };
    persist(updated);
    setNewName('');
  };

  const updateName = (id: string, name: string) => {
    if (!data) return;
    persist({ ...data, competitors: data.competitors.map(c => c.id === id ? { ...c, name } : c) });
  };

  const updateGender = (id: string, gender: 'male' | 'female') => {
    if (!data) return;
    persist({ ...data, competitors: data.competitors.map(c => c.id === id ? { ...c, gender } : c) });
  };

  const deleteCompetitor = (id: string) => {
    if (!data) return;
    if (!window.confirm('Remove this competitor?')) return;
    const competitors = data.competitors.filter(c => c.id !== id);
    // Clear slots that referenced this athlete
    const clearId = (slotId: string | null) => slotId === id ? null : slotId;
    persist({
      ...data,
      competitors,
      qualRound1: data.qualRound1.map(s => ({ ...s, leftId: clearId(s.leftId), rightId: clearId(s.rightId) })),
      qualRound2: data.qualRound2.map(s => ({ ...s, leftId: clearId(s.leftId), rightId: clearId(s.rightId) })),
    });
  };

  const regenerateSchedule = () => {
    if (!data) return;
    if (!window.confirm('This will reset the qualification pairings (not the times). Continue?')) return;
    const { qualRound1, qualRound2 } = generateQualSchedule(data.competitors);
    persist({ ...data, qualRound1, qualRound2 });
  };

  const resetAll = () => {
    if (!window.confirm('Reset ALL speed data? This cannot be undone.')) return;
    const empty = getDefaultData();
    persist({ ...empty, competitors: [], qualRound1: [], qualRound2: [] });
  };

  const clearAllTimes = () => {
    if (!data) return;
    if (!window.confirm('Clear all times? Competitors and pairings will be kept.')) return;
    persist(clearTimes(data));
  };

  // ── Qual time entry ──────────────────────────────────────────────────────

  const setQualTime = (
    round: 'qualRound1' | 'qualRound2',
    slotIdx: number,
    side: 'left' | 'right',
    result: RunResult,
  ) => {
    if (!data) return;
    const slots = data[round].map((s, i) => {
      if (i !== slotIdx) return s;
      return side === 'left' ? { ...s, leftResult: result } : { ...s, rightResult: result };
    });
    persist({ ...data, [round]: slots });
  };

  const setQualAthlete = (
    round: 'qualRound1' | 'qualRound2',
    slotIdx: number,
    side: 'left' | 'right',
    athleteId: string | null,
  ) => {
    if (!data) return;
    const slots = data[round].map((s, i) => {
      if (i !== slotIdx) return s;
      return side === 'left' ? { ...s, leftId: athleteId } : { ...s, rightId: athleteId };
    });
    persist({ ...data, [round]: slots });
  };

  // ── Finals time entry ────────────────────────────────────────────────────

  const setFinalsTime = (
    round: 'mensQuarterFinals' | 'mensSemiFinals' | 'mensFinals' | 'womensSemiFinals' | 'womensFinals',
    matchIdx: number,
    side: 'left' | 'right',
    result: RunResult,
  ) => {
    if (!data) return;
    const matches = data[round].map((m, i) => {
      if (i !== matchIdx) return m;
      return side === 'left' ? { ...m, leftResult: result } : { ...m, rightResult: result };
    });
    persist({ ...data, [round]: matches });
  };

  // ── Derived display data ─────────────────────────────────────────────────
  // NOTE: these are intentionally left as placeholders here; real values are
  // computed after the null-guards below so they never run against null data.

  // ─────────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────────

  const QualTable = ({
    round,
    slots,
    title,
  }: { round: 'qualRound1' | 'qualRound2'; slots: QualSlot[]; title: string }) => (
    <div className="mb-8">
      <h3 className="text-lg font-bold text-dark mb-2">{title}</h3>
      <div className="overflow-x-auto rounded shadow">
        <table className="w-full text-sm border-collapse bg-white">
          <thead>
            <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
              <th className="px-3 py-2 text-left w-36">Time</th>
              <th className="px-3 py-2 text-left">Left Lane Athlete</th>
              <th className="px-3 py-2 text-center w-28">Left Time</th>
              <th className="px-3 py-2 text-left">Right Lane Athlete</th>
              <th className="px-3 py-2 text-center w-28">Right Time</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((slot, i) => {
              const done = (slot.leftResult.time !== null || slot.leftResult.dnf) &&
                           (slot.rightResult.time !== null || slot.rightResult.dnf || !slot.rightId);
              return (
                <tr key={i} className={`border-b ${done ? 'bg-green-50' : 'bg-white'}`}>
                  <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">{slot.timeRange}</td>

                  {/* Left athlete selector */}
                  <td className="px-3 py-2">
                    <select
                      value={slot.leftId ?? ''}
                      onChange={e => setQualAthlete(round, i, 'left', e.target.value || null)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">— bye —</option>
                      {safeData.competitors.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </td>

                  {/* Left time */}
                  <td className="px-3 py-2 text-center">
                    <TimeInput
                      result={slot.leftResult}
                      onChange={r => setQualTime(round, i, 'left', r)}
                    />
                  </td>

                  {/* Right athlete selector */}
                  <td className="px-3 py-2">
                    <select
                      value={slot.rightId ?? ''}
                      onChange={e => setQualAthlete(round, i, 'right', e.target.value || null)}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value="">— bye —</option>
                      {safeData.competitors.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </td>

                  {/* Right time */}
                  <td className="px-3 py-2 text-center">
                    <TimeInput
                      result={slot.rightResult}
                      onChange={r => setQualTime(round, i, 'right', r)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const FinalsTable = ({
    round,
    matches,
    title,
    color = 'bg-primary',
  }: {
    round: 'mensQuarterFinals' | 'mensSemiFinals' | 'mensFinals' | 'womensSemiFinals' | 'womensFinals';
    matches: FinalsMatch[];
    title: string;
    color?: string;
  }) => (
    <div className="mb-8">
      <h3 className={`text-lg font-bold text-white ${color} px-4 py-2 rounded-t`}>{title}</h3>
      <div className="overflow-x-auto rounded-b shadow">
        <table className="w-full text-sm border-collapse bg-white">
          <thead>
            <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
              <th className="px-3 py-2 text-left w-36">Time</th>
              <th className="px-3 py-2 text-left w-28">Match</th>
              <th className="px-3 py-2 text-left">Athlete 1</th>
              <th className="px-3 py-2 text-center w-28">Time 1</th>
              <th className="px-3 py-2 text-left">Athlete 2</th>
              <th className="px-3 py-2 text-center w-28">Time 2</th>
              <th className="px-3 py-2 text-center w-24">Advances</th>
            </tr>
          </thead>
          <tbody>
            {matches.map((match, i) => {
              const done = (match.leftResult.time !== null || match.leftResult.dnf) ||
                           (match.rightResult.time !== null || match.rightResult.dnf);
              return (
                <tr key={i} className={`border-b ${done ? 'bg-green-50' : 'bg-white'}`}>
                  <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">{match.timeRange}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{match.matchLabel}</td>

                  <td className="px-3 py-2 font-semibold">
                    {getName(match.leftId, computed.competitors)}
                    {!match.leftId && <span className="text-gray-400 text-xs italic">{match.matchLabel.split('–')[0]?.trim()}</span>}
                  </td>

                  <td className="px-3 py-2 text-center">
                    <TimeInput
                      result={match.leftResult}
                      onChange={r => setFinalsTime(round, i, 'left', r)}
                    />
                  </td>

                  <td className="px-3 py-2 font-semibold">
                    {getName(match.rightId, computed.competitors)}
                    {!match.rightId && <span className="text-gray-400 text-xs italic">{match.matchLabel.split('–')[1]?.trim()}</span>}
                  </td>

                  <td className="px-3 py-2 text-center">
                    <TimeInput
                      result={match.rightResult}
                      onChange={r => setFinalsTime(round, i, 'right', r)}
                    />
                  </td>

                  <td className="px-3 py-2 text-center">
                    <span className="font-semibold text-secondary text-xs">{match.resultLabel}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ─── render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Loading competition data…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded max-w-lg">
          <strong>Failed to load data</strong><br />
          <span className="text-sm">{error ?? 'Unknown error'}</span><br />
          <button onClick={() => window.location.reload()} className="mt-3 text-sm underline">Retry</button>
        </div>
      </div>
    );
  }

  // data is guaranteed non-null beyond this point
  const safeData = data;
  const computed = autoPopulateBracket(safeData);
  const { men: mensRankings, women: womensRankings } = computeGenderedRankings(safeData);
  const allRankings = computeQualRankings(safeData);
  const hasAnyQualTime = allRankings.some(r => r.bestTime !== null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-dark text-white py-6 px-4 shadow">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Speed Climbing — Admin</h1>
            <p className="text-gray-400 text-sm mt-1">
              {metadata?.name ?? competitionId} ·{' '}
              <a href={`/competition/${competitionId}/speed-schedule`} target="_blank" className="text-blue-300 underline">
                Open public display ↗
              </a>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={clearAllTimes}
              className="px-3 py-1.5 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
            >
              Clear Times
            </button>
            <button
              onClick={resetAll}
              className="px-3 py-1.5 bg-red-700 text-white rounded text-sm hover:bg-red-800"
            >
              Reset All
            </button>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 flex gap-1">
          {(['competitors', 'qualifications', 'finals'] as Tab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-semibold capitalize border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-secondary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-dark'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* ── COMPETITORS TAB ─────────────────────────────────────────── */}
        {activeTab === 'competitors' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-dark">
                Competitors ({data.competitors.length})
              </h2>
              <button
                onClick={regenerateSchedule}
                className="px-4 py-2 bg-secondary text-white rounded text-sm hover:bg-blue-800"
              >
                Regenerate Qual Schedule
              </button>
            </div>

            <div className="bg-white rounded shadow overflow-hidden mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
                    <th className="px-4 py-2 text-left w-8">#</th>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-center w-24">Gender</th>
                    <th className="px-4 py-2 text-center w-24">Run 1</th>
                    <th className="px-4 py-2 text-center w-24">Run 2</th>
                    <th className="px-4 py-2 text-center w-24">Best</th>
                    <th className="px-4 py-2 text-center w-20">Rank</th>
                    <th className="px-4 py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.competitors.map((c, i) => {
                    const rk = allRankings.find(r => r.competitor.id === c.id);
                    const gRankings = c.gender === 'male' ? mensRankings : womensRankings;
                    const gRk = gRankings.find(r => r.competitor.id === c.id);
                    const prefix = c.gender === 'male' ? 'MT' : 'WT';
                    return (
                      <tr key={c.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={c.name}
                            onChange={e => updateName(c.id, e.target.value)}
                            className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <select
                            value={c.gender}
                            onChange={e => updateGender(c.id, e.target.value as 'male' | 'female')}
                            className="border border-gray-200 rounded px-1 py-1 text-xs"
                          >
                            <option value="male">M</option>
                            <option value="female">F</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 text-center font-mono text-gray-600">
                          {rk ? formatTime(rk.run1) : '—'}
                        </td>
                        <td className="px-4 py-2 text-center font-mono text-gray-600">
                          {rk ? formatTime(rk.run2) : '—'}
                        </td>
                        <td className="px-4 py-2 text-center font-mono font-bold text-primary">
                          {rk?.bestTime != null ? `${rk.bestTime.toFixed(2)}s` : '—'}
                        </td>
                        <td className="px-4 py-2 text-center font-bold text-secondary">
                          {hasAnyQualTime && gRk ? `${prefix}${gRk.rank}` : '—'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => deleteCompetitor(c.id)}
                            className="text-red-500 hover:text-red-700 text-xs font-medium"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Add competitor */}
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCompetitor()}
                placeholder="Competitor name"
                className="border border-gray-300 rounded px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-secondary"
              />
              <button
                onClick={addCompetitor}
                disabled={!newName.trim()}
                className="px-4 py-2 bg-secondary text-white rounded text-sm hover:bg-blue-800 disabled:opacity-40"
              >
                + Add
              </button>
            </div>

            <p className="mt-4 text-xs text-gray-400">
              Tip: After editing the competitor list, click "Regenerate Qual Schedule" to auto-assign new pairings.
            </p>
          </div>
        )}

        {/* ── QUALIFICATIONS TAB ──────────────────────────────────────── */}
        {activeTab === 'qualifications' && (
          <div>
            <p className="text-sm text-gray-500 mb-6">
              Enter times in seconds (e.g. <strong>9.45</strong>) or <strong>DNF</strong>.
              Press <kbd className="bg-gray-100 px-1 rounded">Enter</kbd> or click away to save.
            </p>

            <QualTable round="qualRound1" slots={data.qualRound1} title="Round 1 — Qualifications" />
            <QualTable round="qualRound2" slots={data.qualRound2} title="Round 2 — Qualifications" />

            {/* Rankings */}
            {hasAnyQualTime && (
              <div className="space-y-6">
                {[{ label: '♂ Men', rows: mensRankings, prefix: 'MT' }, { label: '♀ Women', rows: womensRankings, prefix: 'WT' }].map(({ label, rows, prefix }) => (
                  <div key={label}>
                    <h3 className="text-lg font-bold text-dark mb-2">{label} Rankings</h3>
                    <div className="overflow-x-auto rounded shadow">
                      <table className="w-full text-sm bg-white">
                        <thead>
                          <tr className="bg-gray-100 text-gray-600 uppercase text-xs tracking-wide">
                            <th className="px-3 py-2 text-left w-16">Seed</th>
                            <th className="px-3 py-2 text-left">Athlete</th>
                            <th className="px-3 py-2 text-center w-24">Run 1</th>
                            <th className="px-3 py-2 text-center w-24">Run 2</th>
                            <th className="px-3 py-2 text-center w-28">Best Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => (
                            <tr key={r.competitor.id} className="border-b">
                              <td className="px-3 py-2 font-bold text-center text-secondary">{prefix}{r.rank}</td>
                              <td className="px-3 py-2 font-semibold">{r.competitor.name}</td>
                              <td className="px-3 py-2 text-center font-mono">{formatTime(r.run1)}</td>
                              <td className="px-3 py-2 text-center font-mono">{formatTime(r.run2)}</td>
                              <td className="px-3 py-2 text-center font-mono font-bold text-primary">
                                {r.bestTime !== null ? `${r.bestTime.toFixed(2)}s` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── FINALS TAB ──────────────────────────────────────────────── */}
        {activeTab === 'finals' && (
          <div>
            {!hasAnyQualTime && (
              <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
                Athlete names in the bracket will appear automatically once qualification times are entered.
              </div>
            )}
            <p className="text-sm text-gray-500 mb-6">
              The bracket is computed automatically from qualification rankings and previous round results.
              Enter times as each match completes.
            </p>

            <h2 className="text-xl font-bold text-dark mb-3 flex items-center gap-2">
              <span className="text-2xl">♂</span> Men's Bracket
            </h2>
            <FinalsTable round="mensQuarterFinals" matches={computed.mensQuarterFinals} title="Quarter Finals" color="bg-secondary" />
            <FinalsTable round="mensSemiFinals"    matches={computed.mensSemiFinals}    title="Semi Finals"    color="bg-primary"   />
            <FinalsTable round="mensFinals"        matches={computed.mensFinals}        title="Finals"         color="bg-dark"      />

            <div className="my-8 border-t" />

            <h2 className="text-xl font-bold text-dark mb-3 flex items-center gap-2">
              <span className="text-2xl">♀</span> Women's Bracket
            </h2>
            <FinalsTable round="womensSemiFinals" matches={computed.womensSemiFinals} title="Semi Finals" color="bg-primary" />
            <FinalsTable round="womensFinals"     matches={computed.womensFinals}     title="Finals"      color="bg-dark"   />
          </div>
        )}
      </div>

      {/* ── Floating Save button ────────────────────────────────────── */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {saving && (
          <span className="bg-dark text-yellow-300 text-xs font-medium px-3 py-1 rounded-full shadow">
            Saving…
          </span>
        )}
        {saved && !saving && (
          <span className="bg-dark text-green-400 text-xs font-medium px-3 py-1 rounded-full shadow">
            ✓ Saved
          </span>
        )}
        {dirty && !saving && (
          <span className="bg-dark text-yellow-200 text-xs px-3 py-1 rounded-full shadow">
            Unsaved changes
          </span>
        )}
        <button
          onClick={saveNow}
          disabled={!dirty || saving}
          className={`flex items-center gap-2 px-5 py-3 rounded-full shadow-lg text-white font-bold text-base transition-all ${
            dirty && !saving
              ? 'bg-green-600 hover:bg-green-700 hover:shadow-xl scale-100'
              : 'bg-gray-400 cursor-not-allowed opacity-60'
          }`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" />
          </svg>
          Save
        </button>
      </div>
    </div>
  );
};

const SpeedAdminPage: React.FC = () => (
  <AdminAccessGate>
    <SpeedAdminInner />
  </AdminAccessGate>
);

export default SpeedAdminPage;
